import type { SimpleSeason } from '@/environment/EnvironmentState';
import { seasonBlend, wrapDay, DAYS_IN_YEAR } from '@/environment/SeasonModel';
import type { DaylightWindow } from '@/environment/SunModel';
import type { IconName } from './Icons';

/**
 * The colour and iconography behind the time/date HUD (see {@link ChronoBar}).
 *
 * Two ribbons, each a picture of the thing it scrubs rather than a generic slider:
 *
 * - **The day** is painted as a sky. Its stops are placed relative to the *actual* sunrise and
 *   sunset for the site's latitude and date, so the daylight band widens in June and narrows in
 *   December, and the golden bands sit where golden hour really is. Drag the date and the sky
 *   redraws itself.
 * - **The year** is painted as a landscape through the seasons: ice blue, blossom, new green, high
 *   summer, harvest gold, rust, bare wood, back to ice.
 *
 * Every function here is pure so the palette can be tested without a DOM, and so the slider thumb,
 * the track gradient, and the compact pill can never disagree about what colour "now" is.
 */

export const MINUTES_PER_DAY = 1440;

/* ------------------------------------- Colour utility ------------------------------------- */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(hex: string): Rgb {
  const value = Number.parseInt(hex.slice(1), 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (n: number): string =>
    Math.round(Math.min(255, Math.max(0, n)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** Blend two hex colours in sRGB. `t` is clamped to 0–1. */
export function mixHex(from: string, to: string, t: number): string {
  const k = Math.min(1, Math.max(0, t));
  const a = parseHex(from);
  const b = parseHex(to);
  return toHex({
    r: a.r + (b.r - a.r) * k,
    g: a.g + (b.g - a.g) * k,
    b: a.b + (b.b - a.b) * k,
  });
}

/**
 * Ink that stays legible on a given fill. The thumb glyph sits directly on the palette colour, which
 * swings from near-black midnight to near-white noon over a single drag, so the glyph has to flip.
 */
export function inkOn(background: string): string {
  const { r, g, b } = parseHex(background);
  // Rec. 601 luma is close enough for a two-way choice and avoids a gamma pass per frame.
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luma > 0.58 ? '#17211a' : '#f2f6fb';
}

interface Stop {
  /** Position along the ribbon, in the ribbon's own units (minutes, or day of year). */
  at: number;
  color: string;
}

/** Sample a sorted stop list, holding the end colours beyond the ends. */
function sampleStops(stops: readonly Stop[], at: number): string {
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (first === undefined || last === undefined) return '#000000';
  if (at <= first.at) return first.color;
  if (at >= last.at) return last.color;
  for (let i = 1; i < stops.length; i++) {
    const lower = stops[i - 1];
    const upper = stops[i];
    if (lower === undefined || upper === undefined) continue;
    if (at <= upper.at) {
      const span = upper.at - lower.at;
      return mixHex(lower.color, upper.color, span <= 0 ? 0 : (at - lower.at) / span);
    }
  }
  return last.color;
}

/** Render stops as a left-to-right CSS gradient, mapping `at` through `toFraction`. */
function gradientCss(stops: readonly Stop[], toFraction: (at: number) => number): string {
  const parts = stops.map((stop) => `${stop.color} ${(toFraction(stop.at) * 100).toFixed(2)}%`);
  return `linear-gradient(90deg, ${parts.join(', ')})`;
}

/* ------------------------------------------ Sky ------------------------------------------- */

const SKY = {
  midnight: '#0a1026',
  night: '#141d3f',
  twilight: '#3b3268',
  rose: '#a85575',
  ember: '#ef8a52',
  gold: '#f7c469',
  pale: '#a9d6f2',
  high: '#62aee6',
} as const;

/**
 * Sky stops for a day, in minutes from midnight.
 *
 * Offsets are a mix of absolute minutes (twilight is roughly the same length of time wherever you
 * are) and fractions of the daylight span (the blue of "morning" scales with the day). Stops are
 * then forced monotonic, because on a short winter day the fractional and absolute stops overlap.
 */
export function skyStops(daylight: DaylightWindow): Stop[] {
  if (daylight.polarNight) {
    return [
      { at: 0, color: SKY.midnight },
      { at: MINUTES_PER_DAY / 2, color: SKY.night },
      { at: MINUTES_PER_DAY, color: SKY.midnight },
    ];
  }
  if (daylight.polarDay) {
    return [
      { at: 0, color: SKY.gold },
      { at: MINUTES_PER_DAY * 0.25, color: SKY.pale },
      { at: MINUTES_PER_DAY / 2, color: SKY.high },
      { at: MINUTES_PER_DAY * 0.75, color: SKY.pale },
      { at: MINUTES_PER_DAY, color: SKY.gold },
    ];
  }

  const rise = daylight.sunriseMinutes ?? 6 * 60;
  const set = daylight.sunsetMinutes ?? 18 * 60;
  const span = Math.max(1, set - rise);

  return monotonic([
    { at: 0, color: SKY.midnight },
    { at: rise - 75, color: SKY.night },
    { at: rise - 38, color: SKY.twilight },
    { at: rise - 14, color: SKY.rose },
    { at: rise + 4, color: SKY.ember },
    { at: rise + 32, color: SKY.gold },
    { at: rise + span * 0.25, color: SKY.pale },
    { at: rise + span * 0.5, color: SKY.high },
    { at: set - span * 0.25, color: SKY.pale },
    { at: set - 34, color: SKY.gold },
    { at: set - 4, color: SKY.ember },
    { at: set + 14, color: SKY.rose },
    { at: set + 40, color: SKY.twilight },
    { at: set + 78, color: SKY.night },
    { at: MINUTES_PER_DAY, color: SKY.midnight },
  ]);
}

/**
 * Force a stop list into strictly increasing positions inside the day, keeping the authored order.
 * Crowded stops collapse toward each other instead of reordering, which is what a polar-ish winter
 * day should look like: the sky runs the whole sequence, just in a couple of hours.
 */
function monotonic(stops: readonly Stop[]): Stop[] {
  const out: Stop[] = [];
  let previous = -1;
  for (const stop of stops) {
    const at = Math.min(MINUTES_PER_DAY, Math.max(previous + 0.5, stop.at));
    if (at <= previous) continue;
    out.push({ at, color: stop.color });
    previous = at;
  }
  return out;
}

/** The sky colour at a moment of the day. */
export function skyColorAt(minutes: number, daylight: DaylightWindow): string {
  return sampleStops(skyStops(daylight), minutes);
}

/** The whole day as one CSS gradient, ready for a track background. */
export function skyGradientCss(daylight: DaylightWindow): string {
  return gradientCss(skyStops(daylight), (at) => at / MINUTES_PER_DAY);
}

/* ----------------------------------------- Year ------------------------------------------- */

/**
 * The year as a colour, anchored on the northern-hemisphere calendar the season model already uses.
 * Deliberately not four flat blocks: the interesting parts of a year are the transitions, so the
 * anchors cluster where colour actually moves (blossom, turn, bare).
 */
const YEAR_STOPS: readonly Stop[] = [
  { at: 1, color: '#a9c3d8' }, // deep winter — ice on the beds
  { at: 40, color: '#b6cbdc' },
  { at: 66, color: '#cfa4c2' }, // blossom
  { at: 90, color: '#9ed46f' }, // new green
  { at: 130, color: '#63bf5c' },
  { at: 172, color: '#2f9d55' }, // high summer
  { at: 215, color: '#54a94a' },
  { at: 248, color: '#c9a93c' }, // late-summer gold
  { at: 275, color: '#e08a35' }, // the turn
  { at: 300, color: '#c25330' }, // rust
  { at: 325, color: '#8d6b57' }, // bare wood
  { at: 350, color: '#8fa8be' },
  { at: DAYS_IN_YEAR, color: '#a9c3d8' },
];

/** The colour of a day of the year. */
export function seasonColorAt(dayOfYear: number): string {
  return sampleStops(YEAR_STOPS, wrapDay(dayOfYear));
}

/** The whole year as one CSS gradient. */
export function seasonGradientCss(): string {
  return gradientCss(YEAR_STOPS, (at) => (at - 1) / (DAYS_IN_YEAR - 1));
}

/* --------------------------------------- Moments ------------------------------------------ */

/** A labelled point on a ribbon: what to call it, what to draw, and what colour it is. */
export interface ChronoMoment {
  label: string;
  icon: IconName;
  color: string;
  /** Legible glyph colour on top of {@link color}. */
  ink: string;
}

/**
 * Name the time of day the way a gardener would — by the light, not by the clock — and pick the
 * glyph that goes with it. The boundaries are anchored to real sunrise/sunset, so "Golden hour" is
 * golden hour on that date at that latitude.
 */
export function timeMoment(minutes: number, daylight: DaylightWindow): ChronoMoment {
  const color = skyColorAt(minutes, daylight);
  const moment = (label: string, icon: IconName): ChronoMoment => ({
    label,
    icon,
    color,
    ink: inkOn(color),
  });

  if (daylight.polarNight) return moment('Polar night', 'moon');
  if (daylight.polarDay) return moment('Midnight sun', 'sun');

  const rise = daylight.sunriseMinutes ?? 6 * 60;
  const set = daylight.sunsetMinutes ?? 18 * 60;
  const span = Math.max(1, set - rise);

  if (minutes < rise - 70 || minutes > set + 70) return moment('Night', 'moon');
  if (minutes < rise - 12) return moment('First light', 'horizon');
  if (minutes < rise + 30) return moment('Sunrise', 'horizon');
  if (minutes < rise + span * 0.34) return moment('Morning', 'sun');
  if (minutes < rise + span * 0.62) return moment('Midday', 'sun');
  if (minutes < set - 55) return moment('Afternoon', 'sun');
  if (minutes < set - 6) return moment('Golden hour', 'horizon');
  if (minutes <= set + 30) return moment('Sunset', 'horizon');
  return moment('Dusk', 'horizon');
}

const SEASON_ICONS: Record<SimpleSeason, IconName> = {
  spring: 'plant',
  summer: 'tree',
  autumn: 'leaf',
  winter: 'snowflake',
};

/**
 * Name a day of the year by where it sits between the two season anchors around it — "Late autumn"
 * rather than a bare "Autumn", because the whole point of a continuous date is that a slider can
 * stop between the seasons.
 *
 * The anchors are the solstices and equinoxes, which astronomically *begin* their season rather than
 * centre it, so the bracket a day falls in is its season and the blend fraction is how far through
 * it that day is. Mid-May comes out "Late spring", which is what it is called.
 */
export function seasonMoment(dayOfYear: number): ChronoMoment {
  const day = wrapDay(dayOfYear);
  const blend = seasonBlend(day);
  const season = blend.from;
  const stage = blend.t < 0.34 ? 'Early' : blend.t < 0.67 ? 'Mid' : 'Late';
  const color = seasonColorAt(day);
  return {
    label: `${stage} ${season}`,
    icon: SEASON_ICONS[season],
    color,
    ink: inkOn(color),
  };
}

/* --------------------------------------- Formatting --------------------------------------- */

/** Minutes from midnight as a clock reading in the viewer's locale (12- or 24-hour). */
export function formatMinutes(minutes: number): string {
  const clamped = Math.min(MINUTES_PER_DAY - 1, Math.max(0, Math.round(minutes)));
  const date = new Date(Date.UTC(2001, 0, 1, Math.floor(clamped / 60), clamped % 60));
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}
